/**
 * Webメール（aurora-mail）の AI 機能を、契約プランと組織ポリシーで制御するための
 * 語彙・契約・純関数。
 *
 * ## なぜコントロールプレーン側で持つのか
 *
 * aurora-mail 自身にもテナント単位の LLM 設定
 * （`PUT /api/v1/organization/llm-provider`）があるが、向こうの実装は
 * **書き込みを server admin に限っている**。向こうのコメントがその理由を
 * 明言している:
 *
 *     Per the product decision, WRITES are server-admin-only — org admins can
 *     VIEW which model their org uses but not change it (central cost/key control).
 *
 * つまり「中央で費用と鍵を管理する」前提で作られている。**その中央がここ**。
 * 契約プラン・上限・従量課金・鍵の保管をコントロールプレーンが持ち、
 * 決まった値を aurora-mail のテナント設定へ同期する。
 * `c:\dev\aurora-mail` は参照専用で変更しない（CLAUDE.md）ので、
 * 向こうに渡す形は**向こうの zod と完全に一致**させる必要がある。
 *
 * ## アップセルの設計
 *
 * AI は原価が出る機能なので、容量や通数と違って「使わせるほど損」になる。
 * だから段階を次の 3 つで切る。
 *
 *   1. **使えない**（`aiAssistant: false`）… 下位プラン。画面にも出さない。
 *   2. **共有のプロバイダを月間上限つきで使える**… 中位。原価は運営が持ち、
 *      上限で頭打ちにする。超過は「止める」か「従量課金」を契約者が選ぶ。
 *   3. **接続先を自分で指定できる**（`aiCustomEndpoint: true`）… 上位。
 *      自社の Ollama / Azure / 地域限定エンドポイントを指定でき、
 *      **鍵も自分のものを使う**（＝原価が契約者側に移り、上限も要らなくなる）。
 *      「社内データを外部 LLM に出せない」要件のある顧客がここへ来る。
 *
 * 3 が実質的にいちばん強いアップセル要因で、同時に**運営の原価が消える**。
 * 上限緩和で売るより筋が良い。
 *
 * ## 守る規律
 *
 * - **API キーの生値をこのパッケージに通さない。** ここに置くのは形だけ。
 *   実体は `ai_provider_credentials` に暗号化して保管し、
 *   同期のときだけ復号して aurora-mail へ渡す（`dkim_key_material` と同じ作法）。
 * - **プロンプト本文と応答は一切扱わない。** aurora-mail 側も
 *   「metadata だけを保存する」契約で作られている（向こうの ai-audit.ts）。
 *   こちらが取り込むのもトークン数と件数だけ。
 * - **上限に達したら機能を止めるのは「ポリシーを none にして同期する」で行う。**
 *   こちら側でカウントを見て弾く実装を足しても、Webメールは向こうの設定で
 *   動くので効かない。真実は 1 か所（＝同期された向こうの設定）にする。
 */
import { z } from 'zod';
import {
  AI_PRESET_PROVIDER,
  AI_PRESET_RESIDENCY_REJECT_JA,
  aiPresetSchema,
  checkBedrockRegionResidency,
  checkPlatformManagedEndpointUrl,
  getAiPreset,
  isPresetAllowedForResidency,
  resolvePresetEndpoint,
  validateModelForPreset,
  type AiPresetOrNone,
} from './ai-presets.js';

/* ==================================================== 語彙 */

/**
 * LLM プロバイダ。**aurora-mail の `ProviderEnum` と 1 文字も違えてはいけない**
 * （`apps/api/src/routes/org-llm-provider.ts` の zod と同じ並び）。
 * 値を足すときは向こうの実装を確認してから。
 */
export const AI_PROVIDER = ['none', 'anthropic', 'openai', 'openai_compat', 'ollama'] as const;
export type AiProvider = (typeof AI_PROVIDER)[number];

/** 契約者が自分の接続先を指定できるプロバイダ（それ以外は運営の共有設定）。 */
export const AI_BYO_PROVIDERS = ['openai_compat', 'ollama'] as const;

/**
 * 機能単位のスイッチ。
 *
 * aurora-mail 側の面と対応させる:
 *   summarize    … スレッド要約（services/llm.ts）
 *   replySuggest … 返信文の提案
 *   nlSearch     … 自然文検索（services/nl-search.ts）
 *   classify     … 自動分類とルール提案（routes/ai-classify.ts）
 *   workflow     … ワークフロー実行（services/workflow-engine.ts）
 *   translate    … 本文の翻訳（routes/ai.ts の task='translate'）
 *
 * **機能ごとに切れるようにする理由**: 要約は本文を丸ごと LLM に渡すが、
 * 分類は件名と差出人だけでも成立する。「本文は出したくないが分類は使いたい」
 * という要件が実際にあるので、一括の on/off にしない。
 *
 * ## `translate` を後から足した理由（2026-08-29）
 *
 * 向こうには `allow_ai_translate` が**前からあり、`routes/ai.ts` が実際に
 * 読んでいる**（`task === 'translate'` の門）。ところがこちらに対応する
 * スイッチが無かったため、**mail-sv は 1 度もこのキーを書いていなかった**
 * ＝向こうのカタログ既定（`true`）のままだった。つまり契約者が画面に出る
 * スイッチを**全部オフにしても、翻訳だけは本文を丸ごと LLM へ送り続ける**。
 * 翻訳は要約と同じく本文全体を渡す機能なので、いちばん止めたい類のもの。
 * **既定は `true`**（向こうの既定と同じ）。`false` にすると、この列を
 * 持たない既存の行が一斉に「翻訳が止まる」に変わってしまう。
 */
export const AI_FEATURE = [
  'summarize',
  'replySuggest',
  'nlSearch',
  'classify',
  'workflow',
  'translate',
] as const;
export type AiFeature = (typeof AI_FEATURE)[number];

/** 月間上限に達したときの挙動。 */
export const AI_LIMIT_ACTION = [
  /** 止める。翌月まで使えない（追加請求が発生しないので既定）。 */
  'block',
  /** 従量課金で使い続ける。請求書に超過分が載る。 */
  'meter',
] as const;
export type AiLimitAction = (typeof AI_LIMIT_ACTION)[number];

/** aurora-mail への同期状態。 */
export const AI_SYNC_STATE = ['pending', 'synced', 'failed', 'not_applicable'] as const;
export type AiSyncState = (typeof AI_SYNC_STATE)[number];

/**
 * 本文をどこまで LLM へ渡すか。aurora-mail の `privacyLevel`
 * （`ai_inference_logs.privacy_level`）に対応させる。
 */
export const AI_PRIVACY_LEVEL = [
  /** 件名・差出人・ラベルなどのメタ情報だけ。 */
  'metadata_only',
  /** 本文の先頭 N 文字まで。 */
  'excerpt',
  /** 本文全体。 */
  'full_body',
] as const;
export type AiPrivacyLevel = (typeof AI_PRIVACY_LEVEL)[number];

/* ---------------------------------------- 接続先の在庫とルーティング（0047） */

/**
 * 接続先を置くリージョン。**運営が在庫を登録するときの語彙**。
 *
 * ここを語彙にしている（自由文字列にしない）のは、距離表
 * （`AI_REGION_DISTANCE_PAIRS`）と対応が取れていないリージョン名を
 * 登録できてしまうと、**そのリージョンだけ「最も遠い」扱いになって
 * 一度も選ばれない**という、症状の出ない壊れ方をするため。
 */
export const AI_REGION = [
  'jp-east',
  'jp-west',
  'ap-northeast',
  'ap-southeast',
  'us-east',
  'us-west',
  'eu-west',
  'eu-central',
] as const;
export type AiRegion = (typeof AI_REGION)[number];

/**
 * 接続先が属する法域。**リージョンとは別に持つ。**
 * `ap-northeast`（ソウル）は日本に近いが `jp` ではない、のような食い違いが
 * 実際にあり、距離の話と法令の話を同じ列で表すと必ず事故になる。
 */
export const AI_DATA_RESIDENCY = ['jp', 'us', 'eu'] as const;
export type AiDataResidency = (typeof AI_DATA_RESIDENCY)[number];

/** 契約者が要求できるレジデンシー。`any` は要求なし。 */
export const AI_RESIDENCY_REQUIREMENT = ['any', ...AI_DATA_RESIDENCY] as const;
export type AiResidencyRequirement = (typeof AI_RESIDENCY_REQUIREMENT)[number];

/** 契約者の希望リージョン。`auto` はメールノードのリージョンから決める。 */
export const AI_PREFERRED_REGION = ['auto', ...AI_REGION] as const;
export type AiPreferredRegion = (typeof AI_PREFERRED_REGION)[number];

/**
 * 接続先の健全性。
 *
 * **`unknown` を作っていない。** 4 値目を作ると「未検査を候補から外す」
 * （＝ヘルスチェックが回っていない環境では 1 件も解決できない）か
 * 「未検査を候補に入れる」（＝ `healthy` と同義）のどちらかにしかならず、
 * どちらの意味でも状態として成立しない。登録時は `healthy` から始め、
 * 疎通が続けて失敗したときにだけ落とす。
 *
 * `degraded` はルーティングからは **`down` と同じく除外される**。
 * 分けてあるのは運用の可視化のため（一時的に揺れているのか、
 * 完全に落ちているのかで運営が採る手が違う）。
 */
export const AI_ENDPOINT_HEALTH = ['healthy', 'degraded', 'down'] as const;
export type AiEndpointHealth = (typeof AI_ENDPOINT_HEALTH)[number];

/* ==================================================== 契約 */

export const aiFeatureFlagsSchema = z.object({
  summarize: z.boolean().default(true),
  replySuggest: z.boolean().default(true),
  nlSearch: z.boolean().default(true),
  classify: z.boolean().default(true),
  workflow: z.boolean().default(false),
  /**
   * **既定は `true`。** 向こうのカタログ既定（`allow_ai_translate`）が `true` で、
   * こちらは今までこのキーを 1 度も書いていなかった。`false` を既定にすると、
   * この設定を触っていない既存の組織で**翻訳だけが黙って止まる**。
   */
  translate: z.boolean().default(true),
});
export type AiFeatureFlags = z.infer<typeof aiFeatureFlagsSchema>;

/**
 * 組織の AI ポリシー。
 *
 * `endpoint` と `hasOwnCredential` を持つのが上位プランの意味。
 * **`apiKey` はここに入らない**（別表で暗号化保管する）。
 */
export const orgAiPolicySchema = z.object({
  enabled: z.boolean().default(false),
  provider: z.enum(AI_PROVIDER).default('none'),
  /** 空文字は「運営の既定モデルに従う」。 */
  model: z.string().max(200).default(''),
  /** `openai_compat` / `ollama` のときだけ意味を持つ。https か、ローカルなら http も。 */
  endpoint: z.string().max(500).default(''),
  /**
   * アグリゲータのプリセット（`packages/shared/src/ai-presets.ts`）。
   * 空文字は「使わない」。**`provider` の enum には混ぜない**
   * （混ぜると aurora-mail へ送れなくなる。理由は ai-presets.ts の冒頭）。
   *
   * 値が入っているときの `provider` は必ず `openai_compat`、
   * `endpoint` はプリセットの baseUrl（自ホスト型は契約者の入力）。
   * その正規化は `normalizePresetPolicy()` が唯一の場所。
   */
  preset: aiPresetSchema.default(''),
  features: aiFeatureFlagsSchema.default({}),
  privacyLevel: z.enum(AI_PRIVACY_LEVEL).default('excerpt'),
  /** 0 = 上限なし。プラン既定を超える値は `resolveAiPolicy` が切り詰める。 */
  monthlyTokenCap: z.number().int().nonnegative().max(10_000_000_000).default(0),
  onLimit: z.enum(AI_LIMIT_ACTION).default('block'),
  /**
   * 推論ログ（メタデータのみ）の保持日数。0 = 運営の既定に従う。
   * **本文は元から保存されない**ので、ここで保持を短くするのは
   * 「誰がいつ何回使ったか」の記録の話。
   */
  logRetentionDays: z.number().int().min(0).max(3650).default(0),

  /* ---- 最寄りエンドポイントへのルーティング（0047） ---- */

  /**
   * どのリージョンの接続先に繋ぎたいか。`auto` は**組織のメールノードの
   * リージョン**から近い順に選ぶ（`resolveAiEndpoint()`）。
   *
   * これは**希望**であって強制ではない。指定したリージョンが落ちていれば
   * 同じ法域の別リージョンへ寄る（それが冗長性の意味）。
   * 「そこ以外へ出さない」を表したいときは `requireResidency` を使う。
   */
  preferredRegion: z.enum(AI_PREFERRED_REGION).default('auto'),

  /**
   * データレジデンシーの**要件**（ハード条件）。`any` 以外を指定すると、
   * その法域の接続先が全滅したときに**黙って別法域へ流さず、AI を止める**。
   *
   * 止まるのが正しい。「社内のメール本文を国外へ出せない」と契約している
   * 相手に対して、障害時だけ国外へ流すのは契約違反であって冗長化ではない。
   */
  requireResidency: z.enum(AI_RESIDENCY_REQUIREMENT).default('any'),
});
export type OrgAiPolicy = z.infer<typeof orgAiPolicySchema>;

/** 契約者が更新できる項目。プロバイダと接続先はプランで弾く。 */
export const orgAiPolicyUpdateSchema = orgAiPolicySchema
  .partial()
  .extend({
    /**
     * 自分の資格情報を差し替えるときだけ載せる。空文字で削除。
     * **保存前に暗号化する。ログにも監査にも生値を出さない。**
     */
    apiKey: z.string().max(500).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '更新する項目がありません');

/** 既定ポリシー（行が無い組織に対して使う）。 */
export function defaultAiPolicy(): OrgAiPolicy {
  return orgAiPolicySchema.parse({});
}

/* ==================================================== 解決 */

export interface AiPlanConstraints {
  /** AI 機能そのものを提供するか。false なら画面にも出さない。 */
  aiAssistant: boolean;
  /** 接続先（endpoint / 自分の鍵）を契約者が指定できるか。 */
  aiCustomEndpoint: boolean;
  /** プランの月間トークン上限（0 = 無制限）。 */
  aiMonthlyTokens: number;
}

/**
 * プランの制約を当てた**実効ポリシー**を返す。唯一の合成点。
 *
 * - `aiAssistant` が false なら、何が設定されていても `enabled=false` /
 *   `provider='none'`。**契約者の設定は消さない**（プランを上げたら戻る）。
 * - `aiCustomEndpoint` が false なら BYO プロバイダと endpoint を落とし、
 *   運営の共有プロバイダに寄せる。どのプロバイダを共有で使うかは
 *   `sharedProvider` で運営が決める（プラットフォーム設定）。
 * - 月間上限はプランの値で**切り詰める**（0 = 無制限は「切り詰めない」）。
 *   契約者が上げるのは許さないが、下げるのは許す（自衛のため）。
 * - **`preferredRegion` と `requireResidency` はプランで落とさない。**
 *   プランを下げたときに `requireResidency` を `any` へ戻すと、
 *   「国外へ出さない」と設定していた組織のデータが、プラン変更を境に
 *   黙って国外へ流れ始める。厳しい側の設定は残し、満たせなければ
 *   `resolveAiEndpoint()` が null を返して止まるほうへ倒す。
 */
export function resolveAiPolicy(
  policy: OrgAiPolicy,
  plan: AiPlanConstraints,
  sharedProvider: { provider: AiProvider; model: string } = { provider: 'none', model: '' },
): OrgAiPolicy {
  if (!plan.aiAssistant) {
    return { ...policy, enabled: false, provider: 'none', endpoint: '', preset: '', monthlyTokenCap: 0 };
  }

  /*
   * **プリセットとレジデンシー要件が矛盾していたら止める。**
   * API と DB の CHECK でも同じ組み合わせを拒んでいるが、ここでも倒しておく。
   * 3 面（画面のプレビュー・運営の説明・同期が実際に送る値）が同じ関数を
   * 通るので、ここで止めれば**どの経路から入った矛盾でも本文が出ない**。
   * 「片方を黙って落として動かし続ける」ほうを選ばないこと（要件を満たして
   * いないのに動いている状態がいちばん危ない）。
   */
  const presetDef = getAiPreset(policy.preset);
  if (presetDef && !isPresetAllowedForResidency(presetDef, policy.requireResidency)) {
    return { ...policy, enabled: false, provider: 'none', endpoint: '', preset: '' };
  }

  const byo = (AI_BYO_PROVIDERS as readonly string[]).includes(policy.provider);
  const provider: AiProvider = byo && !plan.aiCustomEndpoint ? sharedProvider.provider : policy.provider;
  const endpoint = byo && plan.aiCustomEndpoint ? policy.endpoint : '';
  // プリセットは接続先の指定そのものなので、指定できないプランでは落とす。
  const preset: AiPresetOrNone = byo && plan.aiCustomEndpoint ? policy.preset : '';
  const model = provider === sharedProvider.provider && policy.model === '' ? sharedProvider.model : policy.model;

  const cap = clampTokenCap(policy.monthlyTokenCap, plan.aiMonthlyTokens);

  return {
    ...policy,
    provider,
    endpoint,
    preset,
    model,
    monthlyTokenCap: cap,
    // 上限が無制限（0）なら「超過」という概念が無いので block/meter は意味を持たない。
    onLimit: cap === 0 ? 'block' : policy.onLimit,
    enabled: policy.enabled && provider !== 'none',
  };
}

/**
 * 上限の合成。
 *
 * プラン側 0（無制限）… 契約者の指定をそのまま使う（自衛の下限設定を許す）。
 * 契約者側 0（無制限）… プランの上限を当てる。
 * 両方に値… **小さいほう**を採る。
 */
export function clampTokenCap(orgCap: number, planCap: number): number {
  if (planCap === 0) return orgCap;
  if (orgCap === 0) return planCap;
  return Math.min(orgCap, planCap);
}

/* ---------------------------------------- プリセットの正規化（保存の直前） */

export type AiPresetRejectCode =
  /** 知らないプリセット（コード側の表から消えた値が DB に残っている等）。 */
  | 'unknown_preset'
  /** レジデンシー要件と両立しない（アグリゲータは推論先を隠す）。 */
  | 'residency'
  /** 自ホスト型なのに接続先 URL が空。 */
  | 'base_url_required'
  /** モデル名が形に合っていない・空。 */
  | 'model';

export interface AiPresetNormalizeResult {
  ok: boolean;
  /** 正規化後のポリシー（`ok=false` のときは入力のまま）。 */
  policy: OrgAiPolicy;
  code: AiPresetRejectCode | null;
  /** 契約者にそのまま見せる理由。**理由を見せずに 422 を返さない。** */
  message: string | null;
}

/**
 * プリセットを選んだポリシーを、保存できる形へ正規化する。
 * **API（契約者面・運営面）の書き込み経路が必ずここを通る。**
 *
 * `resolveAiPolicy()` とは役割が違うので混ぜないこと。あちらは「読むときの
 * 合成」で DB の値を変えない。こちらは「保存する値を確定させる」ためのもので、
 * `provider` を `openai_compat` に、`endpoint` をプリセットの baseUrl に**書き換える**。
 *
 * ここで確定させておくと、同期側（`toAuroraLlmPayload()`）が畳むときに
 * 追加の推測をしなくて済み、DB に入っている値と向こうへ送る値が一致する。
 * 一致していないと「画面に出ている接続先」と「実際に本文が出ている先」が
 * 食い違い、しかも誰も気付けない。
 */
export function normalizePresetPolicy(policy: OrgAiPolicy): AiPresetNormalizeResult {
  if (policy.preset === '') return { ok: true, policy, code: null, message: null };

  const preset = getAiPreset(policy.preset);
  if (!preset) {
    return {
      ok: false,
      policy,
      code: 'unknown_preset',
      message: '選択されたサービスは現在利用できません',
    };
  }

  if (!isPresetAllowedForResidency(preset, policy.requireResidency)) {
    return { ok: false, policy, code: 'residency', message: AI_PRESET_RESIDENCY_REJECT_JA };
  }

  /*
   * **運営が接続先を持つプリセット（Bedrock）は endpoint を空にして保存する。**
   *
   * 契約者が URL を入力しても**捨てる**（プリセットの目的は「接続先を人に
   * 打たせないこと」なので、ここで採ると意味が消える）。実体は在庫
   * （`ai_provider_endpoints`）で、同期の直前に `resolveAiEndpoint()` が
   * 法域・希望リージョン・health から 1 本選ぶ。
   *
   * 空のまま保存できるのは 0070 で CHECK を緩めたから
   * （`preset = '' OR preset IN (運営管理) OR endpoint <> ''`）。
   * 「空の endpoint を openai_compat として向こうへ送ると既定の OpenAI へ出る」
   * という 0060 の懸念は、**解決できなければ同期そのものを止める**
   * （`provider='none'` で送る）ことで塞いである。
   */
  if (preset.platformManaged) {
    if (policy.enabled) {
      const check = validateModelForPreset(preset, policy.model);
      if (!check.ok) return { ok: false, policy, code: 'model', message: check.message };
    }
    return {
      ok: true,
      policy: { ...policy, provider: AI_PRESET_PROVIDER, endpoint: '' },
      code: null,
      message: null,
    };
  }

  const endpoint = resolvePresetEndpoint(preset, policy.endpoint);
  if (endpoint === null) {
    return {
      ok: false,
      policy,
      code: 'base_url_required',
      message: `${preset.label} は自社で配備するサービスのため、接続先の URL を指定してください`,
    };
  }

  /*
   * モデルは**有効にするときだけ**必須にする。無効のまま途中まで設定を
   * 保存する使い方（鍵だけ先に入れる等）を塞ぐと、画面が不必要に厳しくなる。
   */
  if (policy.enabled) {
    const check = validateModelForPreset(preset, policy.model);
    if (!check.ok) return { ok: false, policy, code: 'model', message: check.message };
  }

  return {
    ok: true,
    policy: { ...policy, provider: AI_PRESET_PROVIDER, endpoint },
    code: null,
    message: null,
  };
}

/**
 * 当月の使用トークンが上限に達しているか。
 *
 * 分母に入れるのは**入力 + 出力**。出力だけを数えると、長文を投げて短い答えを
 * もらう使い方が無料になり、原価（入力トークンにも課金される）と乖離する。
 */
export function isOverTokenCap(
  used: { inputTokens: number; outputTokens: number },
  cap: number,
): boolean {
  if (cap <= 0) return false;
  return used.inputTokens + used.outputTokens >= cap;
}

/**
 * aurora-mail の `PUT /api/v1/organization/llm-provider` に渡す形。
 *
 * **向こうの `UpdateBody` と一致させる**（provider / model / endpoint / apiKey）。
 * 項目を増やして送ると向こうの zod が落とす（`.strict()` ではないが、
 * 増やしても無視されるだけで「効いていないのに設定したつもり」になる）。
 *
 * `enabled=false` は **provider='none' として送る**。向こうに「無効」という
 * 独立したフラグが無いため、これが唯一の止め方。
 */
export interface AuroraLlmProviderPayload {
  provider: AiProvider;
  model?: string;
  endpoint?: string;
  apiKey?: string;
}

export function toAuroraLlmPayload(
  policy: OrgAiPolicy,
  apiKey: string | null,
): AuroraLlmProviderPayload {
  if (!policy.enabled || policy.provider === 'none') {
    return { provider: 'none' };
  }

  /*
   * **プリセットは openai_compat + endpoint へ畳む。**
   * aurora-mail の `ProviderEnum` に `openrouter` は無く、向こうは参照専用
   * （CLAUDE.md）。畳まずに送ると向こうの zod が落として同期だけが失敗する。
   *
   * 畳んだあとに `endpoint` が決まらない（自ホスト型で URL 未入力）なら
   * **止める側へ倒す**。空の endpoint で openai_compat を送ると、向こうは
   * 既定の OpenAI へ出る＝契約者が指定していない先へ本文が流れる。
   */
  const presetDef = getAiPreset(policy.preset);
  if (presetDef) {
    /*
     * 運営が接続先を持つプリセット（Bedrock）では、`policy.endpoint` は
     * **同期の直前に在庫から解決して入れた値**（`aurora-sync.ts` の
     * `withRoute`）。解決できていなければ空のままここへ来るので、
     * 下の `null` 判定で `provider='none'`（＝止める）に落ちる。
     * **空の endpoint を openai_compat として送らない**のがこの分岐の要点で、
     * 送ると向こうは既定の OpenAI へ出る（契約者が選んでいない先へ本文が出る）。
     */
    const endpoint = resolvePresetEndpoint(presetDef, policy.endpoint);
    if (endpoint === null) return { provider: 'none' };
    const folded: AuroraLlmProviderPayload = { provider: AI_PRESET_PROVIDER, endpoint };
    if (policy.model !== '') folded.model = policy.model;
    if (apiKey !== null) folded.apiKey = apiKey;
    return folded;
  }

  const payload: AuroraLlmProviderPayload = { provider: policy.provider };
  if (policy.model !== '') payload.model = policy.model;
  if (policy.endpoint !== '') payload.endpoint = policy.endpoint;
  // null は「変更しない」、空文字は「削除」。**undefined と空文字を混同しないこと。**
  if (apiKey !== null) payload.apiKey = apiKey;
  return payload;
}

/* ==================================================== ポリシーの同期 */

/**
 * 機能フラグを aurora-mail 側のポリシー名へ写す。
 *
 * ## キー名の正本
 *
 * 向こうの **`packages/db/src/policy-eval.ts` の `POLICY_CATALOG`** が正
 * （`apps/api/src/lib/policy-catalog.ts` はその再輸出）。
 * カタログに無いキーは `PUT /api/v1/policies/tenant` が
 * `KNOWN_POLICY_KEYS.has(k)` で**黙って捨てる**（400 にもならない）ので、
 * 名前を間違えると「送っているのに効かない」が無警告で成立する。
 *
 * 以前ここには `ai.summarize.enabled` / `ai.reply_suggest.enabled` /
 * `ai.nl_search.enabled` / `ai.classify.enabled` / `ai.workflow.enabled` の
 * 5 つが書いてあったが、**向こうのカタログにその名前は 1 つも無い**。
 * しかもこの表は定義されているだけで参照が 0 件だった＝機能別スイッチは
 * 1 バイトも送られていなかった。契約者が「要約だけ止める」と設定しても
 * Webメールでは要約が動き続ける、という壊れ方をしていた。
 *
 * ## 写せないもの（`null`）
 *
 * - `nlSearch` … 向こうの自然文検索（`routes/search.ts`）は
 *   **`ai.agent.enabled` だけ**で門を開けている。専用キーが無いので、
 *   これだけを止める手段が向こうに存在しない。
 * - `workflow` … ワークフローの AI ステップ（向こうの
 *   `workers/workflow-execute.ts`）は `ai.agent.enabled` +
 *   `allow_ai_generation` で門を開けている。`replySuggest` と同じキーを
 *   共有しているため、**独立して止められない**。
 *
 * `null` を「送らない」と読むこと。**存在しないキーを送って
 * 「設定できたつもり」にしない。** 向こうに足してほしいキーは
 * `docs/AURORA_MAIL_CHANGES.md` §8 に書いてある。
 */
export const AURORA_POLICY_KEY: Readonly<Record<AiFeature, string | null>> = {
  /** 向こうの `routes/ai.ts`（task='summarize'）が読む。 */
  summarize: 'allow_ai_summary',
  /**
   * 向こうのカタログのラベルは「AI 返信・作成」。
   * ワークフローの AI ステップもこのキーを見る（＝一緒に止まる）。
   */
  replySuggest: 'allow_ai_generation',
  /** 専用キーが向こうに無い。`ai.agent.enabled` で全部まとめて止まるだけ。 */
  nlSearch: null,
  /** 向こうの `routes/ai-classify.ts` が読む。 */
  classify: 'allow_ai_classify',
  /** `allow_ai_generation` と同じ門なので独立して制御できない。 */
  workflow: null,
  /**
   * 向こうの `routes/ai.ts`（`task='translate'`）が読む**専用の門**。
   * キーは前からあったのに**こちらから 1 度も書いていなかった**ので、
   * 契約者が他を全部オフにしても翻訳だけ本文が出続けていた。
   */
  translate: 'allow_ai_translate',
};

/**
 * AI 全体のマスタースイッチ。**向こうで実際に門になっている唯一のキー**
 * （`routes/ai.ts` / `routes/mail.ts` / `routes/search.ts` /
 * `routes/organization.ts` / ワークフローワーカーが読む）。
 *
 * ここを `false` にすると Webメールの AI ボタンごと消える。
 * `provider='none'` だけでは「押せるが失敗する」で、契約者から見ると
 * 「AI を止めたのに画面には出ている」になる。**両方送る。**
 */
export const AURORA_POLICY_MASTER_KEY = 'ai.agent.enabled';

/** 推論ログ（メタデータのみ）の保持日数。向こうの `ai-log-prune` が読む。 */
export const AURORA_POLICY_LOG_RETENTION_KEY = 'ai.log_retention_days';

/**
 * 向こうのカタログが宣言している下限（`min: 90`）。
 *
 * 向こうの `PUT /policies/tenant` は**値の範囲を検査しない**（カタログの
 * min/max は画面のヒントでしかない）ので、90 未満を送れば素通りして
 * 監査証跡が下限を割る。こちらで丸めてから送る。
 */
export const AURORA_POLICY_LOG_RETENTION_MIN_DAYS = 90;

/**
 * **`privacyLevel` は送れない。**
 *
 * こちらの軸は「本文をどこまで渡すか」（`metadata_only` / `excerpt` /
 * `full_body`）。向こうの `privacy_level` は
 * `llm_providers.privacy_level`（`local_only` / `internal` / `external`）で、
 * **接続先がどこに置かれているかを表す別の軸**であり、しかもテナント面の
 * どのルートからも書けない（`PUT /organization/llm-provider` の `UpdateBody`
 * は provider / model / endpoint / apiKey の 4 つだけ、
 * `PUT /policies/tenant` のカタログにも該当キーが無い）。
 *
 * 適当に写すと「本文は出さない設定にしたのに本文が出る」になるので、
 * **写さずに送らないことを明示する**。向こうへの依頼は
 * `docs/AURORA_MAIL_CHANGES.md` §8。
 */
export const AURORA_UNSYNCABLE_FIELDS = ['privacyLevel'] as const;

/**
 * 向こうへ**写せない機能別スイッチ**（`AURORA_POLICY_KEY` が `null` のもの）。
 *
 * 表を二重に持たない（写像表から導出する）。手で並べると、キーを足したときに
 * 片方だけ直して「画面には『効きません』と出ているのに実は効いている」
 * （またはその逆）を作る。
 */
export const AURORA_UNSYNCABLE_FEATURES: readonly AiFeature[] = AI_FEATURE.filter(
  (feature) => AURORA_POLICY_KEY[feature] === null,
);

/**
 * 契約者へそのまま見せる説明。**API が返し、画面が注意書きに使う。**
 *
 * 「送れない」ことをコード内のコメントにだけ書いておくと、契約者から見れば
 * 保存できて画面に残る設定は**効いているようにしか見えない**。
 * 機能別スイッチが 1 バイトも送られていなかった件（`AURORA_POLICY_KEY` の
 * コメント）と同じ壊れ方を、残った 3 項目についても繰り返さないために、
 * **効かないことを応答に載せる**。向こうへの依頼は
 * `docs/AURORA_MAIL_CHANGES.md` §8-2。
 */
export const AURORA_UNSYNCABLE_NOTE_JA =
  'Webメール側に受け口が無いため、これらの指定は現時点では強制されません（設定は保存され、受け口ができ次第そのまま反映されます）。';

/**
 * 「Webメール側の LLM 設定はここから配信されている」ことを画面に出す文言
 * （設計メモ §10-3）。**運営コンソールと契約者パネルの両方で同じ文を使う。**
 *
 * 二重管理に見えることが実害になる。aurora-mail 側にも LLM 設定の画面はあり、
 * そちらで変えても**次の同期で黙って戻る**（mail-sv が冪等に上書きし続ける）。
 * 戻された人には理由が分からないので、こちらの画面に「ここが正本である」と
 * 書いておくのがいちばん安い対処になる。
 *
 * 向こう側で編集そのものを塞ぐ依頼（`PLATFORM_MANAGED_LLM=1` で 409）は
 * `docs/AURORA_MAIL_CHANGES.md` §9。それが入るまでの穴は塞げない。
 */
export const AURORA_MANAGED_LLM_NOTE_JA =
  'Webメールの AI 設定はこの画面から配信されます（Webメール側の画面で変更しても、次回の同期で元に戻ります）。';

/** `PUT /api/v1/policies/tenant` の 1 項目。向こうの正規エンベロープ。 */
export interface AuroraPolicyValue {
  value: boolean | number | string;
}

/**
 * 実効ポリシーを `PUT /api/v1/policies/tenant` の body（`policies`）へ写す。
 *
 * ## エンベロープ
 *
 * **必ず `{ value: … }` で包む。** 向こうの `isTenantFeatureEnabled()` は
 * `raw.value` が boolean のときだけそれを採り、そうでなければ**既定値**
 * （＝ほぼ全部 `true`）に落ちる。生の `false` を送ると、格納はされるのに
 * 読み出しで `true` に戻る＝**止めたつもりで止まらない**。
 *
 * ## マスタースイッチが false のときは全部 false
 *
 * 向こうのカタログ既定は全部 `true` なので、行を消したり送らなかったり
 * すると「有効」に戻る。止めるときは明示的に `false` を書き込む。
 *
 * ## 送らないもの
 *
 * - 写像先が `null` の機能（`nlSearch` / `workflow`）
 * - `privacyLevel`（`AURORA_UNSYNCABLE_FIELDS`）
 * - `logRetentionDays` が 0（＝運営既定に従う）のとき
 */
export function toAuroraPolicyPayload(policy: OrgAiPolicy): Record<string, AuroraPolicyValue> {
  const master = policy.enabled && policy.provider !== 'none';
  const out: Record<string, AuroraPolicyValue> = {
    [AURORA_POLICY_MASTER_KEY]: { value: master },
  };

  for (const feature of AI_FEATURE) {
    const key = AURORA_POLICY_KEY[feature];
    if (key === null) continue;
    // マスターが off なら機能別も off で書く（既定 true に戻らないように）。
    const enabled = master && policy.features[feature];
    // 同じキーに複数の機能が写ることは今は無いが、写ったら**厳しいほうを採る**。
    const prev = out[key];
    out[key] = { value: prev ? Boolean(prev.value) && enabled : enabled };
  }

  if (policy.logRetentionDays > 0) {
    out[AURORA_POLICY_LOG_RETENTION_KEY] = {
      value: Math.max(policy.logRetentionDays, AURORA_POLICY_LOG_RETENTION_MIN_DAYS),
    };
  }

  return out;
}

/* ==================================================== プラットフォーム既定 */

/**
 * 運営が全契約者に対して決めること（`ai_platform_defaults` の 1 行と対応）。
 *
 * **`resolveAiPolicy()` の `sharedProvider` はここから来る。** 契約者が
 * 接続先を指定できないプラン（`aiCustomEndpoint=false`）は、何を設定しても
 * この共有プロバイダに寄せられる。
 *
 * `allowedProviders` が空配列なら制限なし。**空を「全部禁止」と読まない**。
 * 空が全部禁止だと、行を作った直後（既定値は空）に全社の AI が黙って
 * 止まることになる。運営が明示的に列挙したときだけ絞る。
 */
export const aiPlatformDefaultsSchema = z.object({
  provider: z.enum(AI_PROVIDER).default('none'),
  model: z.string().max(200).default(''),
  /** 組織側が 0（無制限）のときに当てる既定。0 = 運営としても無制限。 */
  defaultMonthlyTokenCap: z.number().int().nonnegative().max(10_000_000_000).default(0),
  defaultPrivacyLevel: z.enum(AI_PRIVACY_LEVEL).default('excerpt'),
  allowedProviders: z.array(z.enum(AI_PROVIDER)).max(AI_PROVIDER.length).default([]),
  notes: z.string().max(1000).default(''),
});
export type AiPlatformDefaults = z.infer<typeof aiPlatformDefaultsSchema>;

export function defaultAiPlatformDefaults(): AiPlatformDefaults {
  return aiPlatformDefaultsSchema.parse({});
}

/**
 * 運営の許可リストに載っているか。**`none` は常に許す**
 * （「使わない」を選べなくなるのは不合理）。
 */
export function isAiProviderAllowed(defaults: AiPlatformDefaults, provider: AiProvider): boolean {
  if (provider === 'none') return true;
  if (defaults.allowedProviders.length === 0) return true;
  return defaults.allowedProviders.includes(provider);
}

/* ==================================================== 運営による上書き */

/**
 * 運営が組織のポリシーを上書きするときの契約。
 *
 * 契約者側（`orgAiPolicyUpdateSchema`）との違いは 2 つだけ:
 *   - `lockedByOps` / `opsNote` … **契約者に触らせない**ことを宣言する。
 *   - プランの制約を無視できる（運営は個別に許可できる立場）。
 *
 * `opsNote` を `nullable` にしてあるのは、ロックを外すときに理由も消せる
 * ようにするため。「ロックされていないのに理由だけ残る」は画面が
 * 誤表示する原因になるので、DB の CHECK でも縛ってある（0048）。
 */
export const orgAiPolicyOpsUpdateSchema = orgAiPolicySchema
  .partial()
  .extend({
    apiKey: z.string().max(500).optional(),
    lockedByOps: z.boolean().optional(),
    opsNote: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '更新する項目がありません');

/* ==================================================== 同期の同一性 */

/**
 * 「前に送ったものと同じか」を判定するための正規化文字列。
 *
 * **API キーの生値をここに入れない。** 代わりに指紋（`fingerprint`）を混ぜる。
 * 生値を混ぜると、この文字列を sha256 する前段のメモリ・ログ・例外メッセージに
 * 鍵が乗る経路ができる。指紋でも「鍵が変わったこと」は検出できる。
 *
 * `null` の指紋は「鍵を送らない（＝向こうの既存値を変えない）」を意味し、
 * 空文字は「鍵を消す」を意味する。**両者を同じ扱いにしない**
 * （同じにすると、鍵を消したのに「変更なし」と判定されて消えない）。
 *
 * ハッシュ計算そのもの（`node:crypto`）はここでは行わない。この package は
 * ブラウザ側（admin / portal）からも import されるため。
 */
export function auroraLlmSyncSource(
  payload: AuroraLlmProviderPayload,
  keyFingerprint: string | null,
  /**
   * `toAuroraPolicyPayload()` の結果。**同じハッシュに畳む**のが要点で、
   * 別々のハッシュを持つと「接続先は同期済み・機能別スイッチだけ古い」と
   * いう状態が生まれ、しかもその 2 つを画面に出す場所が無い。
   * 送るのも 1 ジョブなので、同一性の判定も 1 本にする。
   *
   * キー順を固定するのは、`Object.keys` の順序差だけで
   * 「変更あり」と判定して毎回送り直すことにならないようにするため。
   */
  policies?: Readonly<Record<string, AuroraPolicyValue>>,
): string {
  return JSON.stringify([
    payload.provider,
    payload.model ?? '',
    payload.endpoint ?? '',
    'apiKey' in payload ? (keyFingerprint ?? '') : null,
    policies
      ? Object.keys(policies)
          .sort()
          .map((k) => [k, policies[k]?.value ?? null])
      : null,
  ]);
}

/* ============================== 最寄りエンドポイントへのルーティング（0047） */

/**
 * ## なぜ「接続先の在庫」を運営が持つのか
 *
 * 契約者ごとに好きな接続先を指させると、原価も監査もデータレジデンシーも
 * 管理不能になる。aurora-mail 側の実装コメントが言う「中央で費用と鍵を管理する」
 * を実際に成立させるには、**中央が接続先を持っていること**が要る。
 * そのうえで、次の 3 つを同時に満たす必要がある。
 *
 *   - **レイテンシ** … 要約・返信提案は対話的な機能で、往復が遠いと体感が落ちる。
 *   - **データレジデンシー** … 「社内のメール本文を国外へ出せない」は実在の要件。
 *     満たせること自体が上位プランの購入理由になる。
 *   - **冗長性** … 1 リージョンの障害で Webメールの AI が全滅しない。
 *
 * ## 判定をここ（純関数）に集める理由
 *
 * 同じ判定が「契約者画面のプレビュー」「運営画面の説明」「同期ワーカーが
 * 実際に送る値」の 3 か所で要る。3 か所に書くと必ずずれ、**ずれても誰も
 * 気付かない**（画面は「jp-east に繋がっています」と出しているのに、
 * 実際は us-east に流れている、が成立してしまう）。
 * 距離も緯度経度ではなく静的な表にしてあるのは、運用で調整できる形にするため
 * （実測したら jp-west → ap-northeast のほうが近かった、が普通に起きる）。
 */

/**
 * リージョン間の静的な距離表（単位は無い相対値）。
 *
 * **緯度経度を持ち込まない。** 実効的な近さは物理距離ではなく回線の引き回しで
 * 決まるので、実測に合わせて運用で書き換えられる形にしておく。
 * 対称とみなす（片方向だけ書く）。表に無い組み合わせは
 * `AI_REGION_DISTANCE_UNKNOWN`。
 */
const AI_REGION_DISTANCE_PAIRS: ReadonlyArray<readonly [AiRegion, AiRegion, number]> = [
  ['jp-east', 'jp-west', 1],
  ['jp-east', 'ap-northeast', 2],
  ['jp-west', 'ap-northeast', 2],
  ['jp-east', 'ap-southeast', 5],
  ['jp-west', 'ap-southeast', 5],
  ['ap-northeast', 'ap-southeast', 5],
  ['jp-east', 'us-west', 8],
  ['jp-west', 'us-west', 8],
  ['ap-northeast', 'us-west', 9],
  ['ap-southeast', 'us-west', 10],
  ['jp-east', 'us-east', 12],
  ['jp-west', 'us-east', 12],
  ['ap-northeast', 'us-east', 13],
  ['ap-southeast', 'us-east', 13],
  ['us-east', 'us-west', 3],
  ['us-east', 'eu-west', 6],
  ['us-east', 'eu-central', 7],
  ['us-west', 'eu-west', 10],
  ['us-west', 'eu-central', 11],
  ['eu-west', 'eu-central', 1],
  ['jp-east', 'eu-west', 14],
  ['jp-west', 'eu-west', 14],
  ['jp-east', 'eu-central', 14],
  ['jp-west', 'eu-central', 14],
  ['ap-northeast', 'eu-west', 14],
  ['ap-northeast', 'eu-central', 14],
  ['ap-southeast', 'eu-west', 12],
  ['ap-southeast', 'eu-central', 12],
];

/** 表に無い組み合わせの距離。**同点にせず必ず最下位へ落とす**ための値。 */
export const AI_REGION_DISTANCE_UNKNOWN = 99;

const AI_REGION_DISTANCE = new Map<string, number>();
for (const [a, b, d] of AI_REGION_DISTANCE_PAIRS) {
  AI_REGION_DISTANCE.set(a + '|' + b, d);
  AI_REGION_DISTANCE.set(b + '|' + a, d);
}

/**
 * リージョン間の距離。同じなら 0、表に無ければ `AI_REGION_DISTANCE_UNKNOWN`。
 * **未知を 0 にしない**（知らない組み合わせが「最も近い」になってしまう）。
 */
export function aiRegionDistance(from: string, to: string): number {
  if (from === to) return 0;
  return AI_REGION_DISTANCE.get(from + '|' + to) ?? AI_REGION_DISTANCE_UNKNOWN;
}

/**
 * 資格情報の参照名。
 *
 * `ai_provider_credentials` の一意キーは `(organization_id, provider)` なので、
 * そのままだと**同じプロバイダの接続先を 2 つ登録したときに鍵を分けられない**
 * （jp の openai_compat と us の openai_compat が別ベンダー、は普通にある）。
 * そこで共有鍵に限り `openai_compat@tokyo-vendor` のような**スロット付きの
 * 名前**を許す。組織の鍵は今までどおり素のプロバイダ名。
 *
 * スロット名まで含めて `ai_provider_credentials.provider` に入るので、
 * 0047 で CHECK 制約をこの形に合わせて緩めてある。
 */
export const AI_CREDENTIAL_REF_RE =
  /^(anthropic|openai|openai_compat|ollama)(@[a-z0-9][a-z0-9-]{0,30})?$/;

/** 参照名から基のプロバイダを取り出す。形が不正なら null。 */
export function parseAiCredentialRef(ref: string): AiProvider | null {
  const matched = AI_CREDENTIAL_REF_RE.exec(ref);
  const base = matched?.[1];
  if (!base) return null;
  return base as AiProvider;
}

/** 接続先が実際に使う資格情報の参照名。空なら**プロバイダ名そのもの**。 */
export function aiCredentialRefOf(endpoint: {
  provider: AiProvider;
  credentialRef: string;
}): string {
  return endpoint.credentialRef === '' ? endpoint.provider : endpoint.credentialRef;
}

/** 運営が登録する接続先 1 件（`ai_provider_endpoints` の行に対応）。 */
export interface AiEndpointCandidate {
  id: string;
  code: string;
  provider: AiProvider;
  region: string;
  endpoint: string;
  /** 空なら「組織の指定 or プラットフォーム既定のモデルに従う」。 */
  model: string;
  /** 小さいほど優先。同点の最終手段。 */
  priority: number;
  enabled: boolean;
  /**
   * **null = 法域が確定しない**（アグリゲータのプリセット）。
   * null は `requireResidency` が `any` 以外の組織からは**必ず外れる**
   * （`c.dataResidency === requirement` が成立しない）。これは意図した挙動で、
   * 「分からない」を「満たしている」に読み替えないための表現。
   */
  dataResidency: AiDataResidency | null;
  health: AiEndpointHealth;
  /** 未計測は null。**0 と混同しない**（0 は「計測済みで極めて速い」）。 */
  latencyP50Ms: number | null;
  credentialRef: string;
  /**
   * この在庫のプリセット（0060 / 0070）。省略は `''`（手入力の接続先）と同じ。
   *
   * **省略可能にしてあるのは既存の呼び出し側を壊さないためだけ**で、
   * DB から読むときは必ず入れること（入れないと、Bedrock を選んだ組織の
   * 候補が 1 件も見つからず「接続先が無い」で止まる）。
   */
  preset?: AiPresetOrNone;
}

/** 未計測のレイテンシを比較に載せるときの値。計測済みが必ず勝つ。 */
export const AI_LATENCY_UNKNOWN = 1_000_000;

export interface AiRoutingInput {
  /** 実効ポリシーのプロバイダ。`none` なら解決しない。 */
  provider: AiProvider;
  requireResidency: AiResidencyRequirement;
  preferredRegion: AiPreferredRegion;
  /** 組織のメールノードのリージョン。分からなければ null。 */
  nodeRegion: string | null;
  /**
   * 実効ポリシーのプリセット。**在庫のプリセットと完全一致でしか選ばない。**
   * 省略は `''`（プリセットを使わない）と同じ。理由は
   * `resolveAiEndpoint()` の 1' 番の説明を読むこと。
   */
  requirePreset?: AiPresetOrNone;
}

export type AiRoutingRejectReason =
  /** そもそも AI を使わない設定。 */
  | 'provider_none'
  /** そのプロバイダの接続先を運営が 1 件も登録していない。 */
  | 'no_endpoint_for_provider'
  /**
   * プリセット（Bedrock 等）の在庫を運営が 1 件も登録していない。
   * **`no_endpoint_for_provider` と分けてある**のは、運営が採る手が違うから
   * （「OpenAI 互換の在庫はあるが Bedrock のゲートウェイが無い」を、
   * 「接続先が 1 本も無い」と同じ文言で通知すると必ず調査が空振りする）。
   */
  | 'no_endpoint_for_preset'
  /** レジデンシー要件を満たす接続先が無い。**別法域へ流さない**。 */
  | 'residency_unsatisfied'
  /** 候補はあるが全部 healthy でない。 */
  | 'all_unhealthy';

export interface AiRoutingDecision {
  endpoint: AiEndpointCandidate | null;
  reason: AiRoutingRejectReason | null;
  /** 順位付けの対象になった件数。 */
  eligible: number;
  /** 上位の候補 id（説明と監査に出す）。 */
  ranked: readonly string[];
}

/**
 * 最寄りの接続先を決める。**この関数が唯一の判定点。**
 *
 * 順序（設計メモ C と同じ）:
 *   1. `requireResidency` が `any` でなければ、その法域だけに絞る（**ハード条件**）
 *   2. `preferredRegion` が `auto` でなければ、そのリージョンを最優先
 *   3. `auto` ならメールノードのリージョンから距離の近い順
 *   4. `health='healthy'` のものだけ。同点は latency → priority → id
 *   5. 全滅したら **null**（＋理由）。呼び出し側はポリシーを止めて運営へ通知する
 *
 * **1 だけがハード条件**なのが要点。2 と 3 は順位付けにしか使わないので、
 * 希望リージョンが落ちていれば同じ法域の別リージョンへ自動で寄る（冗長性）。
 * 一方で法域は決して跨がない（**黙って国外へ流さない**）。
 * 「近いほうを選ぶ」と「出してはいけない先に出さない」を同じ強さで扱うと、
 * 障害のたびに契約と食い違う経路ができる。
 */
export function resolveAiEndpoint(
  candidates: readonly AiEndpointCandidate[],
  input: AiRoutingInput,
): AiRoutingDecision {
  const none = { endpoint: null, eligible: 0, ranked: [] as readonly string[] };
  if (input.provider === 'none') return { ...none, reason: 'provider_none' };

  const byProvider = candidates.filter((c) => c.enabled && c.provider === input.provider);
  if (byProvider.length === 0) return { ...none, reason: 'no_endpoint_for_provider' };

  /*
   * 1'. **プリセットは完全一致でしか選ばない**（0070）。
   *
   * プリセットの在庫（Bedrock のゲートウェイ、アグリゲータ）は provider が
   * どれも `openai_compat` なので、プロバイダだけで絞ると
   *   - Bedrock を選んだ組織が、運営が別件で登録した OpenAI 互換ベンダーへ
   *   - 逆に、手入力の接続先を使う組織が Bedrock のゲートウェイへ
   * 流れうる。どちらも**契約者が選んだのと違う先へ本文が出る**という、
   * レジデンシー以前の壊れ方なので、両方向で一致を要求する。
   */
  const wantPreset: AiPresetOrNone = input.requirePreset ?? '';
  const byPreset = byProvider.filter((c) => (c.preset ?? '') === wantPreset);
  if (byPreset.length === 0) {
    return { ...none, reason: wantPreset === '' ? 'no_endpoint_for_provider' : 'no_endpoint_for_preset' };
  }

  const byResidency =
    input.requireResidency === 'any'
      ? byPreset
      : byPreset.filter((c) => c.dataResidency === input.requireResidency);
  if (byResidency.length === 0) return { ...none, reason: 'residency_unsatisfied' };

  const healthy = byResidency.filter((c) => c.health === 'healthy');
  if (healthy.length === 0) return { ...none, reason: 'all_unhealthy' };

  // `auto` ならノードのリージョンが基準。分からなければ距離では並べない
  // （全部 0 として扱い、latency と priority だけで決める）。
  const base = input.preferredRegion !== 'auto' ? input.preferredRegion : input.nodeRegion;

  const preferHit = (c: AiEndpointCandidate): number =>
    input.preferredRegion !== 'auto' && c.region === input.preferredRegion ? 0 : 1;
  const dist = (c: AiEndpointCandidate): number => (base ? aiRegionDistance(base, c.region) : 0);
  const latency = (c: AiEndpointCandidate): number => c.latencyP50Ms ?? AI_LATENCY_UNKNOWN;

  const ranked = [...healthy].sort((a, b) => {
    const byPreferred = preferHit(a) - preferHit(b);
    if (byPreferred !== 0) return byPreferred;
    const byDistance = dist(a) - dist(b);
    if (byDistance !== 0) return byDistance;
    const byLatency = latency(a) - latency(b);
    if (byLatency !== 0) return byLatency;
    const byPriority = a.priority - b.priority;
    if (byPriority !== 0) return byPriority;
    // **最後は id で決め切る。** ここを入れないと同点の並びが実行ごとに変わり、
    // 「同じ設定なのに繋がる先が毎回違う」という再現しない挙動になる。
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  return {
    endpoint: ranked[0] ?? null,
    reason: null,
    eligible: ranked.length,
    ranked: ranked.slice(0, 5).map((c) => c.id),
  };
}

/**
 * 運営が接続先を登録・更新するときの契約。
 *
 * `endpoint` の SSRF 検査はここでは行わない（`assertOutboundUrlAllowed()` は
 * API 側にしか無い）。**呼び出し側で必ず通すこと。**
 */
export const aiProviderEndpointInputSchema = z.object({
  /** 運営が付ける短い識別名（`openai-jp-east` など）。一意。 */
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, '英小文字・数字・ハイフンで指定してください'),
  /** `none` は在庫として意味を持たない。 */
  provider: z.enum(['anthropic', 'openai', 'openai_compat', 'ollama']),
  region: z.enum(AI_REGION),
  /**
   * 空を許すのは**プリセットが baseUrl を持っているとき**だけ
   * （`checkAiEndpointShape()` が組み合わせを見る）。
   * 固定の baseUrl を人に打たせないのがプリセットの目的なので、
   * ここで min(1) を強制すると打ち間違いの経路を残すことになる。
   */
  endpoint: z.string().max(500).default(''),
  model: z.string().max(200).default(''),
  priority: z.number().int().min(0).max(1000).default(100),
  enabled: z.boolean().default(true),
  /**
   * **null を許すのはプリセット（アグリゲータ）の在庫だけ。**
   * 組み合わせの検査は `checkAiEndpointShape()`（zod では表せない）。
   */
  dataResidency: z.enum(AI_DATA_RESIDENCY).nullable().default(null),
  /**
   * アグリゲータのプリセット（空文字 = 従来どおりの手入力）。
   * 入っているときの `provider` は `openai_compat` に限る。
   */
  preset: aiPresetSchema.default(''),
  /** 空なら `provider` と同名の共有鍵を使う。 */
  credentialRef: z
    .union([z.literal(''), z.string().max(64).regex(AI_CREDENTIAL_REF_RE, '資格情報の参照名の形式が不正です')])
    .default(''),
  /** 疎通確認で叩くパス（`/v1/models` など）。空なら `endpoint` そのもの。 */
  healthCheckPath: z.string().max(200).default(''),
  notes: z.string().max(1000).default(''),
});
export type AiProviderEndpointInput = z.infer<typeof aiProviderEndpointInputSchema>;

/**
 * 在庫 1 件の組み合わせ検査。**zod で表せない条件だけをここに置く。**
 *
 * 見るのは 4 つ:
 *   1. プリセットは `openai_compat` の在庫にしか付けられない
 *      （プリセットは全部 OpenAI 互換なので、他の provider に付いていたら
 *      設定ミス。同期時に向こうで 4xx になるまで気付けない）。
 *   2. 接続先 URL は、プリセットが baseUrl を持たないときは必須。
 *   3. **法域が確定しないプリセット（アグリゲータ）は `data_residency` を NULL に。**
 *      ここで運営が `jp` などと申告できてしまうと、契約者のレジデンシー要件を
 *      「実際には保証されていないのに満たしている」ことにできてしまう。
 *      これは 0060 の CHECK 制約でも同じことを止めている。
 *   4. 逆に、法域が分かる接続先で NULL は許さない（誰にも選ばれなくなる）。
 *
 * 戻り値は**日本語の理由**（null なら問題なし）。運営画面にそのまま出す。
 */
export function checkAiEndpointShape(input: {
  provider: string;
  endpoint: string;
  preset: AiPresetOrNone;
  dataResidency: AiDataResidency | null;
  /**
   * `AI_REGION` の値。**運営が持つプリセット（Bedrock）のときだけ見る**
   * （リージョンが法域を決めるので、対応表と食い違っていないかを検査する）。
   * 省略したときは検査しない（既存の呼び出し側を壊さないため）。
   */
  region?: string;
}): string | null {
  const preset = input.preset === '' ? null : getAiPreset(input.preset);
  if (input.preset !== '' && !preset) return '選択されたサービスは現在利用できません';

  if (preset) {
    if (input.provider !== AI_PRESET_PROVIDER) {
      return `${preset.label} は OpenAI 互換のサービスです。プロバイダは ${AI_PRESET_PROVIDER} を選んでください`;
    }
    /*
     * **運営が持つプリセット（Bedrock）の在庫だけの検査。**
     *
     *   - 接続先はゲートウェイの内部エンドポイントなので、SSRF ガードではなく
     *     `checkPlatformManagedEndpointUrl()` で見る（私設アドレスは許し、
     *     クラウドのメタデータサービスだけを拒む。理由は向こうのコメント）。
     *   - リージョンと法域は**対応表どおりでなければ拒む**。ここを人の申告に
     *     任せると、`us-east-1` の在庫に `jp` と書けてしまい、
     *     レジデンシー要件を満たしていないのに満たしていることにできる。
     */
    if (preset.platformManaged) {
      if (input.endpoint.trim() === '') {
        return `${preset.label} はゲートウェイの接続先 URL を指定してください`;
      }
      const urlError = checkPlatformManagedEndpointUrl(input.endpoint);
      if (urlError) return urlError;
      if (input.region !== undefined) {
        const regionError = checkBedrockRegionResidency(input.region, input.dataResidency);
        if (regionError) return regionError;
      } else if (input.dataResidency === null) {
        return '法域（データレジデンシー）を指定してください';
      }
      return null;
    }

    const resolved = resolvePresetEndpoint(preset, input.endpoint);
    if (resolved === null) {
      return `${preset.label} は自社で配備するサービスのため、接続先の URL を指定してください`;
    }
    if (preset.residencyUnknown && input.dataResidency !== null) {
      return `${preset.label} は実際の処理先を公開しないため、法域（データレジデンシー）を指定できません`;
    }
    if (!preset.residencyUnknown && input.dataResidency === null) {
      return '法域（データレジデンシー）を指定してください';
    }
    return null;
  }

  if (input.endpoint.trim() === '') return '接続先の URL を指定してください';
  if (input.dataResidency === null) return '法域（データレジデンシー）を指定してください';
  return null;
}

/**
 * 在庫に登録するときの実効 URL。プリセットなら**その baseUrl が正**。
 * 運営が別の URL を打っていても定義側で上書きする（打ち間違いを構造で潰す）。
 */
export function resolveAiEndpointUrl(input: { endpoint: string; preset: AiPresetOrNone }): string {
  const preset = input.preset === '' ? null : getAiPreset(input.preset);
  if (!preset) return input.endpoint;
  return resolvePresetEndpoint(preset, input.endpoint) ?? input.endpoint;
}

/** 部分更新。`code` は付け替えない（運営の手順書や監視の紐付けが切れる）。 */
export const aiProviderEndpointPatchSchema = aiProviderEndpointInputSchema
  .omit({ code: true })
  .partial()
  .extend({
    /** この接続先が使う共有鍵の登録・差し替え。空文字で削除。**レスポンスには出ない。** */
    apiKey: z.string().max(500).optional(),
    /** 運営が手で健全性を戻す（原因を直した直後に次の疎通を待たない）。 */
    health: z.enum(AI_ENDPOINT_HEALTH).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '更新する項目がありません');
